import React, { useState, useEffect, useRef } from 'react';
import { Card, Spin, Row, Col, Table, Tag, Button, message, Space } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { yuan, percent, zoneTagColor, zoneHexColor, ZONE_META } from '../utils/format.js';
import { apiFetch, apiDownload, onDataChanged } from '../utils/api.js';

const zoneBg = (z) => ZONE_META.find(x => x.key === z)?.bg || '#ffffff';

// 省份简称（地图 label 用，去掉省/市/自治区等后缀）
const shortProvince = (n) => String(n || '').replace(/(省|市|壮族自治区|回族自治区|维吾尔自治区|自治区|特别行政区)$/, '');

// 全国订单分布地图 option（订单量分级着色 + 省份标注占比 + 悬浮凸出高亮）
function buildMapOption(data) {
  const total = data.reduce((a, d) => a + (Number(d.order_count) || 0), 0);
  const mapData = data.map(d => ({ name: d.province, value: Number(d.order_count) || 0 }));
  const maxV = Math.max(1, ...mapData.map(d => d.value));
  const pctOf = (v) => (total > 0 ? (Number(v) / total * 100) : 0);
  return {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#F0E6DE', borderWidth: 1, padding: [10, 14],
      textStyle: { color: '#26201C', fontSize: 13 },
      formatter: (p) => {
        if (!p.value) return p.name;
        return '<div style="font-weight:700;margin-bottom:6px;">' + p.name + '</div>'
          + '<div>订单量：<b style="color:#D85A30;">' + Number(p.value).toLocaleString() + '</b> 单</div>'
          + '<div>占比：<b style="color:#D85A30;">' + pctOf(p.value).toFixed(2) + '%</b></div>';
      },
    },
    visualMap: {
      min: 0, max: maxV, left: 20, bottom: 20,
      text: ['高', '低'], textStyle: { color: '#8c8c8c' },
      inRange: { color: ['#FBE3D6', '#F3C4AA', '#E89A72', '#D85A30', '#B34418'] },
      calculable: true,
    },
    series: [{
      name: '订单量', type: 'map', map: 'china', roam: true, zoom: 1.05,
      scaleLimit: { min: 0.8, max: 5 },
      label: {
        show: true, fontSize: 10, color: '#fff',
        textBorderColor: 'rgba(70,32,16,0.65)', textBorderWidth: 2, lineHeight: 13,
        formatter: (p) => {
          if (!p.value) return '';
          return shortProvince(p.name) + '\n' + pctOf(p.value).toFixed(1) + '%';
        },
      },
      itemStyle: { borderColor: '#ffffff', borderWidth: 1, areaColor: '#F6EDE6' },
      emphasis: {
        label: {
          show: true, color: '#fff', fontSize: 12, fontWeight: 700,
          textBorderColor: 'rgba(70,32,16,0.8)', textBorderWidth: 2,
          formatter: (p) => {
            if (!p.value) return p.name;
            return p.name + '\n' + Number(p.value).toLocaleString() + ' 单 · ' + pctOf(p.value).toFixed(2) + '%';
          },
        },
        itemStyle: {
          areaColor: '#C2471A', borderColor: '#ffffff', borderWidth: 2,
          shadowBlur: 24, shadowColor: 'rgba(216,90,48,0.55)', shadowOffsetY: 10,
        },
      },
      select: { label: { show: true, color: '#fff' }, itemStyle: { areaColor: '#B34418' } },
      data: mapData,
    }],
  };
}

// 中国地图组件（裸 echarts：fetch 注册地图 + init + setOption）
function ChinaMap({ data }) {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const [geoReady, setGeoReady] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch('/china.json').then(r => r.json()).then(geo => {
      if (!alive) return;
      echarts.registerMap('china', geo);
      setGeoReady(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const c = echarts.init(ref.current);
    chartRef.current = c;
    const onResize = () => c.resize();
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); c.dispose(); chartRef.current = null; };
  }, []);

  useEffect(() => {
    if (geoReady && chartRef.current && data.length) chartRef.current.setOption(buildMapOption(data), true);
  }, [geoReady, data]);

  return <div ref={ref} style={{ width: '100%', height: 560 }} />;
}

// 省份排行榜（排名 + 省份 + 数值 + 占比 + 进度条）
function RankList({ title, list, valueKey, total, unit, valueColor, emptyText }) {
  const medalBg = (i) => (i === 0 ? '#F5A623' : i === 1 ? '#9BA6B2' : i === 2 ? '#C77B3F' : '#F0E6DE');
  const medalColor = (i) => (i < 3 ? '#fff' : '#8C8178');
  return (
    <Card title={title} size="small" style={{ borderRadius: 10 }} styles={{ body: { padding: '8px 12px 12px' } }}>
      <div style={{ maxHeight: 380, overflowY: 'auto', paddingRight: 4 }}>
        {list.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#bfbfbf', padding: '40px 0' }}>{emptyText || '暂无数据'}</div>
        ) : list.map((d, i) => {
          const v = Number(d[valueKey]) || 0;
          const pct = total > 0 ? (v / total * 100) : 0;
          return (
            <div key={d.province} style={{ padding: '7px 0', borderBottom: i < list.length - 1 ? '1px solid #F5EDE8' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: medalBg(i), color: medalColor(i), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#26201C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.province}</span>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: valueColor, fontVariantNumeric: 'tabular-nums' }}>{unit === '单' ? v.toLocaleString() : '¥' + v.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>{pct.toFixed(1)}%</div>
                </div>
              </div>
              <div style={{ height: 4, background: '#F5EDE8', borderRadius: 2, margin: '6px 0 0 32px' }}>
                <div style={{ height: '100%', width: pct.toFixed(1) + '%', background: valueColor, borderRadius: 2, opacity: 0.85 }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function RegionAnalysis() {
  const [data, setData] = useState([]);
  const [zoneSummary, setZoneSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = () => apiFetch('/api/analysis/region')
      .then(r => r.json())
      .then(d => { setData(d.data); setZoneSummary(d.zoneSummary || []); setLoading(false); })
      .catch(() => setLoading(false));
    load();
    return onDataChanged(() => { setLoading(false); load(); }); // 数据变更静默刷新
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiDownload('/api/export/report', '电商管理工作台_报表.xlsx');
      message.success('报表已导出');
    } catch (e) {
      message.error(e.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  // 按区域汇总
  const regionSummary = {};
  for (const d of data) {
    if (!regionSummary[d.region_view]) {
      regionSummary[d.region_view] = { region: d.region_view, orders: 0, revenue: 0, profit: 0, provinces: 0 };
    }
    regionSummary[d.region_view].orders += d.order_count;
    regionSummary[d.region_view].revenue += d.revenue;
    regionSummary[d.region_view].profit += d.profit;
    regionSummary[d.region_view].provinces++;
  }
  const regionList = Object.values(regionSummary).sort((a, b) => b.revenue - a.revenue);

  // 省份销量 / 利润排名（含占比）
  const totalOrders = data.reduce((a, d) => a + (Number(d.order_count) || 0), 0);
  const totalProfit = data.reduce((a, d) => a + (Number(d.profit) || 0), 0);
  const orderRank = [...data].sort((a, b) => (Number(b.order_count) || 0) - (Number(a.order_count) || 0)).slice(0, 15);
  const profitRank = [...data].sort((a, b) => (Number(b.profit) || 0) - (Number(a.profit) || 0)).slice(0, 15);

  // 区域对比图
  const regionBarOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: ['营收', '利润'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: regionList.map(r => r.region) },
    yAxis: [{ type: 'value', name: '金额(元)' }, { type: 'value', name: '订单数' }],
    series: [
      { name: '营收', type: 'bar', itemStyle: { color: '#D85A30' }, data: regionList.map(r => r.revenue) },
      { name: '利润', type: 'bar', itemStyle: { color: '#CF1322' }, data: regionList.map(r => r.profit) },
      { name: '订单数', type: 'line', yAxisIndex: 1, itemStyle: { color: '#3F8600' }, data: regionList.map(r => r.orders) },
    ],
  };

  // 省份热力图（柱状图）
  const provinceData = [...data].sort((a, b) => b.revenue - a.revenue);
  const provinceBarOption = {
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      formatter: params => {
        const p = provinceData[params[0].dataIndex];
        return `${p.province}<br/>区域: ${p.region_view}<br/>订单: ${p.order_count}单<br/>营收: ¥${yuan(p.revenue)}<br/>利润: ¥${yuan(p.profit)}<br/>毛利率: ${percent(p.avg_margin, 3)}%`;
      },
    },
    grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
    xAxis: { type: 'category', data: provinceData.map(p => p.province), axisLabel: { rotate: 45, fontSize: 11 } },
    yAxis: { type: 'value', name: '营收(元)' },
    dataZoom: [{ type: 'slider', start: 0, end: 100 }],
    series: [{
      type: 'bar',
      data: provinceData.map(p => ({
        value: p.revenue,
        itemStyle: { color: zoneHexColor(p.region_view) },
      })),
    }],
  };

  const columns = [
    { title: '区域', dataIndex: 'region_view', width: 70,
      render: v => <Tag color={zoneTagColor(v)}>{v}</Tag> },
    { title: '省份', dataIndex: 'province', width: 110 },
    { title: '订单数', dataIndex: 'order_count', width: 80, align: 'right', sorter: (a, b) => a.order_count - b.order_count },
    { title: '营收', dataIndex: 'revenue', width: 110, align: 'right',
      sorter: (a, b) => a.revenue - b.revenue, defaultSortOrder: 'descend',
      render: v => <span style={{ color: '#D85A30', fontWeight: 600 }}>¥{yuan(v)}</span> },
    { title: '成本', dataIndex: 'cost', width: 100, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '运费', dataIndex: 'shipping', width: 90, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '利润', dataIndex: 'profit', width: 100, align: 'right',
      sorter: (a, b) => a.profit - b.profit,
      render: v => <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(v)}</span> },
    { title: '毛利率', dataIndex: 'avg_margin', width: 90, align: 'right',
      sorter: (a, b) => a.avg_margin - b.avg_margin,
      render: v => `${percent(v, 3)}%` },
  ];

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>区域分析</div>
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>运费已按订单所属区域（一区/二区/三区/四区）匹配商品对应区运费</div>
        </div>
        <Button type="primary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
          导出报表（含区域运费）
        </Button>
      </div>

      <Row gutter={[24, 20]} style={{ marginBottom: 20 }}>
        {regionList.map(r => (
          <Col xs={12} md={8} key={r.region}>
            <Card size="small" style={{ borderRadius: 10 }} styles={{ body: { padding: '16px 20px' } }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Tag color={zoneTagColor(r.region)} style={{ fontSize: 14, padding: '2px 12px' }}>{r.region}</Tag>
                  <div style={{ marginTop: 10, color: '#8c8c8c', fontSize: 12 }}>{r.provinces}个省份 · {r.orders}单</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#D85A30' }}>¥{yuan(r.revenue)}</div>
                  <div style={{ fontSize: 14, color: '#CF1322', fontWeight: 600 }}>¥{yuan(r.profit)}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title="全国订单销售分布"
        extra={<span style={{ fontSize: 12, color: '#8c8c8c' }}>省份按订单量分级着色 · 悬停查看订单量与占比</span>}
        style={{ marginBottom: 16 }}
      >
        <ChinaMap data={data} />
        <div style={{ textAlign: 'center', color: '#bfbfbf', fontSize: 12, marginTop: 8 }}>
          地图数据采用国家标准行政区划边界（含台湾省、香港、澳门及南海诸岛）
        </div>
      </Card>

      <Row gutter={[24, 20]} style={{ marginBottom: 20 }}>
        <Col xs={24} md={12}>
          <RankList title="省份销量排名 TOP15" list={orderRank} valueKey="order_count" total={totalOrders} unit="单" valueColor="#D85A30" />
        </Col>
        <Col xs={24} md={12}>
          <RankList title="省份利润排名 TOP15" list={profitRank} valueKey="profit" total={totalProfit} unit="元" valueColor="#CF1322" />
        </Col>
      </Row>

      {zoneSummary.length > 0 && (
        <Card title="各区域运费汇总（按一区 / 二区 / 三区 / 四区）" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            {zoneSummary.map(z => (
              <Col xs={24} md={8} key={z.region} style={{ marginBottom: 12 }}>
                <Card size="small" style={{ background: zoneBg(z.region) }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tag color={zoneTagColor(z.region)} style={{ fontSize: 13, padding: '2px 10px' }}>{z.region}</Tag>
                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>{z.order_count}单 · {yuan(z.quantity)}件</span>
                  </div>
                  <Row gutter={8} style={{ marginTop: 10 }}>
                    <Col span={12}>
                      <div style={{ color: '#8c8c8c', fontSize: 12 }}>总运费</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#EF9F27' }}>¥{yuan(z.shipping)}</div>
                    </Col>
                    <Col span={12}>
                      <div style={{ color: '#8c8c8c', fontSize: 12 }}>平均单件运费</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#D4537E' }}>¥{yuan(z.avg_freight_per_unit)}</div>
                    </Col>
                    <Col span={12} style={{ marginTop: 8 }}>
                      <div style={{ color: '#8c8c8c', fontSize: 12 }}>营收</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#D85A30' }}>¥{yuan(z.revenue)}</div>
                    </Col>
                    <Col span={12} style={{ marginTop: 8 }}>
                      <div style={{ color: '#8c8c8c', fontSize: 12 }}>利润</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#CF1322' }}>¥{yuan(z.profit)}</div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      <Card title="区域营收对比" style={{ marginBottom: 16 }}>
        <ReactECharts option={regionBarOption} theme="brand" style={{ height: 320 }} />
      </Card>

      <Card title="各省份营收分布（颜色区分区域）" style={{ marginBottom: 16 }}>
        <ReactECharts option={provinceBarOption} theme="brand" style={{ height: 400 }} />
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey={(r) => `${r.region_view}-${r.province}`}
        size="small"
        pagination={{ pageSize: 20, showTotal: t => `共 ${t} 条` }}
      />
    </div>
  );
}
